import React, { useState, useEffect, Suspense, useRef } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import GridViewIcon from '@mui/icons-material/GridView';
import Paper from '@mui/material/Paper';
import { styled, alpha } from '@mui/material/styles';
import appService from '../../../services/ApplicationService';
import { useParams } from 'react-router-dom';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StarBorder from '@mui/icons-material/StarBorder';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import Divider from '@mui/material/Divider';
import KeyIcon from '@mui/icons-material/Key';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import appPasswordService from '../../../services/PasswordService';
import InfoIcon from '@mui/icons-material/Info';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LogoLoader from '../../common/LogoLoader';
import CertificationBanner from '../../common/CertificationBanner';
import NarrativeVersionHistory from '../../common/NarrativeVersionHistory';
import NarrativeCertificationDialog from '../../common/NarrativeCertificationDialog';
import NarrativeVersionCompare from '../../common/NarrativeVersionCompare';
import processNarrativeService from '../../../services/ProcessNarrativeService';
import EditIcon from '@mui/icons-material/Edit';

// Styled Components
const NarrativeContainer = styled(mui.Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  minHeight: '85vh',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
}));

const SectionCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f0f0f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const ActionButton = styled(mui.Button)(({ theme }) => ({
  margin: theme.spacing(1),
  textTransform: 'none',
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
}));

const DownloadButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: '#4caf50',
  color: 'white',
  '&:hover': {
    backgroundColor: '#388e3c',
  },
}));

const PrintButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: '#2196f3',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1976d2',
  },
}));

const ShareButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: '#ff9800',
  color: 'white',
  '&:hover': {
    backgroundColor: '#f57c00',
  },
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    // Add space for the edit button
    justifyContent: 'space-between',
}));

const SidebarLink = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

const InfoBlock = styled(mui.Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const StatusChip = styled(mui.Chip)(({ theme, status }) => ({
  backgroundColor: 
    status === 'enabled' ? alpha('#4caf50', 0.1) :
    status === 'disabled' ? alpha('#f44336', 0.1) : 
    alpha('#9e9e9e', 0.1),
  color: 
    status === 'enabled' ? '#4caf50' :
    status === 'disabled' ? '#f44336' : 
    '#9e9e9e',
  fontWeight: 500,
  border: 
    status === 'enabled' ? `1px solid ${alpha('#4caf50', 0.2)}` :
    status === 'disabled' ? `1px solid ${alpha('#f44336', 0.2)}` : 
    `1px solid ${alpha('#9e9e9e', 0.2)}`,
}));

const MainPaper = styled(mui.Paper)(({ theme }) => ({
  maxHeight: '85vh',
  minHeight: '85vh',
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
}));

const SectionContent = styled(mui.Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const SectionSubtitle = styled(mui.Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

// Create a new component for the section edit button
const SectionEditButton = ({ sectionId, isEditMode, onClick }) => (
    <mui.Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => onClick(sectionId)}
        startIcon={<EditIcon fontSize="small" />}
        sx={{
            height: 32,
            fontSize: '0.75rem',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
                boxShadow: 'none',
            }
        }}
    >
        Edit Section
    </mui.Button>
);

const SectionHeading = ({ icon, title, sectionId, isEditMode, onSectionEdit }) => (
    <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
            {title}
        </mui.Box>
        {!isEditMode && (
            <SectionEditButton 
                sectionId={sectionId} 
                isEditMode={isEditMode} 
                onClick={onSectionEdit} 
            />
        )}
    </mui.Box>
);

const ProcessNarrativeDetails = () => {
    console.log("Component rendering started");
   
        const { id } = useParams();
        const { company } = useParams();
        
        // Extract the section parameter from the URL if present
        const urlParams = new URLSearchParams(window.location.search);
        const sectionParam = urlParams.get('section');
        
        // Log the route parameters to help with debugging
        console.log("ProcessNarrativeDetails - Route Parameters:", { id, company, sectionParam });
        
        // Define default company name if not provided in the URL
        const [companyName, setCompanyName] = useState(company || 'Unknown');
        
        const [apps, setApps] = useState([]);
        const [network, setNetwork] = useState([]);
        const [pwdata, setPWData] = useState([]);
        const [provisioningData, setProvisioningData] = useState([]);
        const [terminationData, setTerminationData] = useState([]);
        const [uarData, setUarData] = useState({});
        const [privilegedData, setPrivilegedData] = useState({});
        const [sidebarVisible, setSidebarVisible] = useState(true);
        const [open, setOpen] = React.useState(true);
        const [activeSection, setActiveSection] = useState(sectionParam || 'authentication');
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [isPdfGenerating, setIsPdfGenerating] = useState(false);
        const narrativeRef = useRef(null);
        const navigate = useNavigate();
        
        // Add new state variables for narrative versioning
        const [certificationDialogOpen, setCertificationDialogOpen] = useState(false);
        const [compareDialogOpen, setCompareDialogOpen] = useState(false);
        const [compareVersionIds, setCompareVersionIds] = useState([null, null]);
        const [currentVersionId, setCurrentVersionId] = useState(null);
        const [isEditMode, setIsEditMode] = useState(false);
        const [editedContent, setEditedContent] = useState({});
        
        // Move fetchData function definition outside useEffect so it can be referenced elsewhere
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                console.log("Starting data fetch for app ID:", id);
                // Fetch apps and users in parallel
                const appsResponse = await appService.fetchAppsById(id);
                console.log("Apps data response:", appsResponse);
                
                if (appsResponse) {
                    setApps(appsResponse);
                    
                    // Update company name if not provided in URL
                    if (!company && appsResponse.COMPANY_NAME) {
                        setCompanyName(appsResponse.COMPANY_NAME);
                    }
                    
                    // Fetch network data only if apps data has NETWORK property
                    if (appsResponse.NETWORK) {
                        try {
                            const networkResponse = await appService.fetchAppsById(appsResponse.NETWORK);
                            console.log("Network data response:", networkResponse);
                            if (networkResponse) {
                                setNetwork(networkResponse);
                            }
                        } catch (error) {
                            console.error(`Error fetching network data: ${error.message}`);
                        }
                    }
                } else {
                    console.error("Apps response was falsy:", appsResponse);
                    setError("Unable to load application data. Please try again later.");
                }
            } catch (fetchError) {
                console.error(`Error fetching app data: ${fetchError.message}`, fetchError);
                setError("Failed to load application data. Please check your connection and try again.");
            }
            
            try {
                // Fetch password data
                const pwDataResponse = await appPasswordService.fetchAppPasswordbyID(id);
                if (pwDataResponse) {
                    setPWData(pwDataResponse);
                    console.log("Password data loaded:", pwDataResponse);
                }
            } catch (error) {
                console.error(`Error fetching password data: ${error.message}`);
            }
            
            try {
                // Fetch provisioning data
                const provisioningDataResponse = await appService.getProvisioningProcessByID(id);
                if (provisioningDataResponse) {
                    setProvisioningData(provisioningDataResponse);
                    console.log("Provisioning data loaded:", provisioningDataResponse);
                }
            } catch (error) {
                console.error(`Error fetching provisioning data: ${error.message}`);
            }
            
            try {
                // Fetch termination data
                const terminationDataResponse = await appService.getTerminationProcessByID(id);
                if (terminationDataResponse) {
                    setTerminationData(terminationDataResponse);
                    console.log("Termination data loaded:", terminationDataResponse);
                }
            } catch (error) {
                console.error(`Error fetching termination data: ${error.message}`);
            }
            
            try {
                // Fetch UAR data
                console.log("About to fetch UAR data for ID:", id);
                const uarDataResponse = await appService.getUARProcessByID(id);
                console.log("Raw UAR data response:", uarDataResponse);
                if (uarDataResponse) {
                    setUarData(uarDataResponse);
                    console.log("UAR data loaded and set:", uarDataResponse);
                } else {
                    console.warn("UAR data response was empty or null");
                    setUarData({});
                }
            } catch (error) {
                console.error(`Error fetching UAR data: ${error.message}`);
                setUarData({});
            }
            
            try {
                // Fetch privileged account data
                console.log("About to fetch privileged account data for ID:", id);
                const privilegedDataResponse = await appService.getAdminProcessByID(id);
                console.log("Raw privileged data response:", privilegedDataResponse);
                if (privilegedDataResponse) {
                    setPrivilegedData(privilegedDataResponse);
                    console.log("Privileged account data loaded and set:", privilegedDataResponse);
                } else {
                    console.warn("Privileged data response was empty or null");
                    setPrivilegedData({});
                }
            } catch (error) {
                console.error(`Error fetching privileged account data: ${error.message}`);
                setPrivilegedData({});
            }
            
            // Add code to check certification status
            try {
                // Fetch certification status for this application
                const certStatus = await processNarrativeService.checkCertificationStatus(id);
                
                // Add logic to handle certification status if needed
                console.log("Certification status:", certStatus);
            } catch (certError) {
                console.error("Error fetching certification status:", certError);
            }
            
            setLoading(false);
            console.log("All data fetching complete, loading set to false");
        };

        // Various handler functions that use fetchData
        const handleClick = () => {
            setOpen(!open);
        };

        const toggleSidebar = () => {
            setSidebarVisible(!sidebarVisible);
        };

        const scrollToSection = (sectionId) => {
            const section = document.getElementById(sectionId);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
                setActiveSection(sectionId);
            }
        };

        const handleDownloadPDF = async () => {
            if (!narrativeRef.current || isPdfGenerating) return;

            try {
                // Show loading state
                setIsPdfGenerating(true);
                
                // Create a print-optimized version in a new window
                const printWindow = window.open('', '_blank');
                
                if (!printWindow) {
                    alert("Please allow pop-ups to generate the PDF view");
                    setIsPdfGenerating(false);
                    return;
                }
                
                // Get the original styles
                const styles = Array.from(document.styleSheets)
                    .map(styleSheet => {
                        try {
                            return Array.from(styleSheet.cssRules)
                                .map(rule => rule.cssText)
                                .join('\n');
                        } catch (e) {
                            console.log('Error accessing styleSheet rules');
                            return '';
                        }
                    })
                    .join('\n');
                
                // Clone content
                const content = narrativeRef.current.cloneNode(true);
                
                // Write the content to the new window
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${apps.APP_NAME || 'Process Narrative'} - PDF Export</title>
                        <style>
                            ${styles}
                            
                            /* Print-specific styles */
                            @page {
                                size: A4;
                                margin: 15mm;
                            }
                            body {
                                font-family: Arial, sans-serif;
                                background-color: white !important;
                                color: black !important;
                            }
                            /* Force each section to start on a new page */
                            [id^="authentication"], [id^="provisioning"], 
                            [id^="termination"], [id^="privileged"], 
                            [id^="access-review"] {
                                page-break-before: always;
                            }
                            /* Prevent tables and cards from breaking across pages */
                            table, .MuiPaper-root, .info-block {
                                page-break-inside: avoid;
                            }
                            /* Force specific heights to auto for proper content flow */
                            .MuiPaper-root, .MuiBox-root {
                                max-height: none !important;
                                height: auto !important;
                                overflow: visible !important;
                            }
                            /* Ensure headings don't break */
                            h1, h2, h3, h4, h5, h6 {
                                page-break-after: avoid;
                            }
                            /* Hide UI elements not needed for print */
                            button, .MuiButton-root, .navigation, footer {
                                display: none !important;
                            }
                            /* Make sure text is visible */
                            * {
                                color: black !important;
                                text-shadow: none !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            /* Keep backgrounds for visual design */
                            .MuiPaper-root, .info-block, .section-card {
                                box-shadow: none !important;
                                border: 1px solid #ddd !important;
                            }
                            /* Fix layout issues */
                            .MuiGrid-container {
                                display: block !important;
                            }
                            .MuiGrid-item {
                                width: 100% !important;
                                max-width: 100% !important;
                                flex-basis: 100% !important;
                            }
                        </style>
                    </head>
                    <body>
                        <div id="print-container" style="width: 100%; padding: 0; margin: 0; background-color: white;">
                            <h1 style="text-align: center; margin-bottom: 20px; font-size: 24pt;">${apps.APP_NAME || 'Process Narrative'}</h1>
                        </div>
                    </body>
                    </html>
                `);
                
                // Append the content to the print container
                const printContainer = printWindow.document.getElementById('print-container');
                printContainer.appendChild(content);
                
                // Wait for content to load and then print
                printWindow.document.close();
                
                printWindow.onload = function() {
                    setTimeout(() => {
                        printWindow.focus();
                        printWindow.print();
                        
                        // Clean up after printing is done or canceled
                        const checkPrintDialogClosed = setInterval(() => {
                            if (printWindow.closed) {
                                clearInterval(checkPrintDialogClosed);
                                setIsPdfGenerating(false);
                            }
                        }, 1000);
                        
                        // Set a timeout to stop checking after a reasonable time
                        setTimeout(() => {
                            clearInterval(checkPrintDialogClosed);
                            setIsPdfGenerating(false);
                        }, 60000); // 1 minute max wait
                    }, 1000);
                };
                
            } catch (error) {
                console.error("Error in print preparation:", error);
                setIsPdfGenerating(false);
                
                // Fallback to basic print
                window.print();
            }
        };

        const handlePrint = () => {
            window.print();
        };

        const handleShare = () => {
            // This would be expanded with actual sharing functionality
            // For now, we'll just show a snackbar
            // You could implement email sharing, link generation, etc.
            alert("Share functionality will be implemented based on organization requirements.");
        };

        useEffect(() => {
            console.log("useEffect for fetchData triggered");
            fetchData();
        }, [id, company]); // Include company in the dependency array

        // Add debug output in the component
        useEffect(() => {
            if (!loading) {
                console.log("Current state:");
                console.log("apps:", apps);
                console.log("pwdata:", pwdata);
                console.log("provisioningData:", provisioningData);
                console.log("terminationData:", terminationData);
                console.log("uarData:", uarData);
                console.log("privilegedData:", privilegedData);
                console.log("network:", network);
            }
        }, [loading, apps, pwdata, provisioningData, terminationData, uarData, privilegedData, network]);

        // Add debug logging for UAR and Privileged data specifically
        useEffect(() => {
            if (!loading) {
                console.log("UAR data specifically:", uarData);
                console.log("Privileged data specifically:", privilegedData);
                console.log("UAR data type:", typeof uarData);
                console.log("Privileged data type:", typeof privilegedData);
                
                // Log out what keys exist in these objects
                if (uarData) console.log("UAR data keys:", Object.keys(uarData));
                if (privilegedData) console.log("Privileged data keys:", Object.keys(privilegedData));
            }
        }, [loading, uarData, privilegedData]);

        // Scroll to the active section when data is loaded
        useEffect(() => {
            if (!loading) {
                setTimeout(() => {
                    scrollToSection(activeSection);
                }, 300); // Small delay to ensure DOM is fully rendered
            }
        }, [loading, activeSection]);

        console.log("About to prepare main content");

        // Create a theme for consistent styling
        const theme = createTheme({
            palette: {
                primary: {
                    main: '#1976d2',
                },
                secondary: {
                    main: '#10b981',
                },
                text: {
                    primary: '#111827',
                    secondary: '#6B7280',
                },
                divider: 'rgba(0, 0, 0, 0.06)',
            },
            typography: {
                fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            },
            shape: {
                borderRadius: 8,
            }
        });

        // Function to handle certification process
        const handleCertify = () => {
            setCertificationDialogOpen(true);
        };
        
        const handleCertificationComplete = async () => {
            // Refresh certification status and narrative data
            try {
                // Fetch updated certification status
                const status = await processNarrativeService.checkCertificationStatus(id);
                
                // Refresh application data if needed
                // This ensures we have the latest data after certification
                fetchData();
            } catch (error) {
                console.error("Error refreshing data after certification:", error);
            }
        };
        
        // Function to handle version comparison
        const handleCompareVersions = (versionId1, versionId2) => {
            setCompareVersionIds([versionId1, versionId2]);
            setCompareDialogOpen(true);
        };
        
        // Function to view specific version
        const handleViewVersion = async (versionId) => {
            try {
                setLoading(true);
                
                // Fetch the specific version
                const versionData = await processNarrativeService.fetchNarrativeVersion(versionId);
                
                // Update the state with this version's data
                // This would replace current app data with the version data
                if (versionData) {
                    // Store current data for potential restoration
                    setCurrentVersionId(versionId);
                    
                    // Update the displayed data with version data
                    // This assumes versionData contains sections similar to what's loaded initially
                    if (versionData.authentication) setApps(versionData.authentication);
                    if (versionData.password) setPWData(versionData.password);
                    if (versionData.provisioning) setProvisioningData(versionData.provisioning);
                    if (versionData.termination) setTerminationData(versionData.termination);
                    if (versionData.uar) setUarData(versionData.uar);
                    if (versionData.privileged) setPrivilegedData(versionData.privileged);
                }
            } catch (error) {
                console.error("Error viewing version:", error);
            } finally {
                setLoading(false);
            }
        };
        
        // Function to restore a version
        const handleRestoreVersion = async (versionId) => {
            try {
                setLoading(true);
                
                // Restore this version as the current version
                await processNarrativeService.createNarrativeVersion(id, {
                    version_id: versionId,
                    restore: true,
                    notes: "Restored from previous version"
                });
                
                // Refresh the data to show the restored version
                fetchData();
            } catch (error) {
                console.error("Error restoring version:", error);
            } finally {
                setLoading(false);
            }
        };
        
        // Function to toggle edit mode
        const handleToggleEditMode = () => {
            if (isEditMode) {
                // If switching from edit mode to view mode, discard changes
                setIsEditMode(false);
                setEditedContent({});
            } else {
                // If switching to edit mode, prepare edited content with current data
                setIsEditMode(true);
                setEditedContent({
                    authentication: apps,
                    password: pwdata,
                    provisioning: provisioningData,
                    termination: terminationData,
                    uar: uarData,
                    privileged: privilegedData
                });
            }
        };
        
        // Function to save edited content
        const handleSaveEdits = async () => {
            try {
                setLoading(true);
                
                // Create a new version with edited content
                await processNarrativeService.createNarrativeVersion(id, {
                    content: editedContent,
                    notes: "User-edited narrative"
                });
                
                // Exit edit mode and refresh data
                setIsEditMode(false);
                setEditedContent({});
                fetchData();
                
            } catch (error) {
                console.error("Error saving narrative edits:", error);
            } finally {
                setLoading(false);
            }
        };

        // Function to handle editing specific sections
        const handleSectionEdit = (sectionId) => {
            // Navigate to the corresponding setup page based on the section
            switch(sectionId) {
                case 'authentication':
                    navigate(`/applications/setupinfo/${id}`);
                    break;
                case 'provisioning':
                    // Update to use /applications/controls/ for provisioning
                    navigate(`/applications/controls/${id}?section=provisioning`);
                    break;
                case 'termination':
                    // Update to use /applications/controls/ for termination
                    navigate(`/applications/controls/${id}?section=termination`);
                    break;
                case 'privileged':
                    // Update to use /applications/controls/ for privileged
                    navigate(`/applications/controls/${id}?section=admin`);
                    break;
                case 'access-review':
                    // Update to use /applications/controls/ for access-review
                    navigate(`/applications/controls/${id}?section=uar`);
                    break;
                default:
                    // If no specific section, go to general setup
                    navigate(`/applications/setupinfo/${id}`);
            }
        };

        // Create main content to be wrapped by the sidebar
        const mainContent = (
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/processnarrative">
                            Process Narrative
                        </mui.Link>
                        <mui.Typography color="text.primary">{companyName}</mui.Typography>
                        <mui.Typography color="text.primary">{apps.APP_NAME}</mui.Typography>
                    </mui.Breadcrumbs>

                    {/* Add Certification Banner here */}
                    {!loading && apps.APP_NAME && (
                        <CertificationBanner 
                            appId={id} 
                            onCertify={handleCertify}
                        />
                    )}

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <AutoStoriesIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                {apps.APP_NAME ? apps.APP_NAME : 'Process Narrative'}
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Process narrative report for access security controls and compliance documentation.
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2
                    }}>
                        {/* Version History button */}
                        <mui.Box>
                            <NarrativeVersionHistory 
                                appId={id}
                                onViewVersion={handleViewVersion}
                                onCompareVersions={handleCompareVersions}
                                onRestoreVersion={handleRestoreVersion}
                            />
                            
                            {currentVersionId && (
                                <mui.Button 
                                    variant="text" 
                                    color="primary" 
                                    size="small"
                                    onClick={fetchData}
                                    sx={{ ml: 1 }}
                                >
                                    Return to Current Version
                                </mui.Button>
                            )}
                        </mui.Box>

                        <mui.Box>
                            {isEditMode ? (
                                <>
                                    <mui.Button 
                                        variant="outlined" 
                                        color="secondary" 
                                        size="small"
                                        onClick={handleToggleEditMode}
                                        sx={{ mr: 1 }}
                                    >
                                        Cancel
                                    </mui.Button>
                                    <mui.Button 
                                        variant="contained" 
                                        color="primary" 
                                        size="small"
                                        onClick={handleSaveEdits}
                                    >
                                        Save Changes
                                    </mui.Button>
                                </>
                            ) : (
                                <mui.Button 
                                    variant="outlined" 
                                    color="primary" 
                                    size="small"
                                    onClick={handleToggleEditMode}
                                    startIcon={<EditIcon />}
                                    sx={{ mr: 1 }}
                                >
                                    Edit Narrative
                                </mui.Button>
                            )}
                            
                            <DownloadButton 
                                startIcon={isPdfGenerating ? 
                                    <mui.CircularProgress size={20} thickness={4} color="inherit" /> : 
                                    <DownloadIcon />}
                                onClick={handleDownloadPDF}
                                disabled={isPdfGenerating}
                                size="small"
                            >
                                {isPdfGenerating ? 'Generating...' : 'Download PDF'}
                            </DownloadButton>
                            
                            <PrintButton 
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                size="small"
                                disabled={isPdfGenerating}
                            >
                                Print
                            </PrintButton>
                            
                            <ShareButton 
                                startIcon={<ShareIcon />}
                                onClick={handleShare}
                                size="small"
                                disabled={isPdfGenerating}
                            >
                                Share
                            </ShareButton>
                        </mui.Box>
                    </mui.Box>

                    <Separator />

                    <NarrativeContainer>
                        <mui.Grid
                            container 
                            spacing={3}
                            direction="row"
                            alignItems="flex-start"
                        >
                            <mui.Grid item
                                xs={12} md={sidebarVisible ? 3 : 1}
                                sx={{
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <MainPaper elevation={3} sx={{ p: 0 }}>
                                    <mui.Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        p: 2,
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                        {sidebarVisible ? (
                                            <mui.Typography variant="subtitle1" fontWeight={600}>
                                                Navigation
                                            </mui.Typography>
                                        ) : null}
                                        <mui.IconButton 
                                            onClick={toggleSidebar}
                                            size="small"
                                            sx={{ 
                                                ...(sidebarVisible ? {} : { margin: 'auto' }),
                                            }}
                                        >
                                            <MenuIcon fontSize="small" />
                                        </mui.IconButton>
                                    </mui.Box>
                                    
                                    <List
                                        component="nav"
                                        sx={{ p: 1 }}
                                    >
                                        <SidebarLink 
                                            active={activeSection === 'authentication'} 
                                            onClick={() => scrollToSection('authentication')}
                                        >
                                            <ListItemIcon sx={{ minWidth: sidebarVisible ? '40px' : '0px', m: sidebarVisible ? 0 : 'auto' }}>
                                                <mui.Tooltip title={!sidebarVisible ? "Authentication" : ""}>
                                                    <SecurityIcon color={activeSection === 'authentication' ? 'primary' : 'inherit'} />
                                                </mui.Tooltip>
                                            </ListItemIcon>
                                            {sidebarVisible && (
                                                <ListItemText 
                                                    primary="Authentication" 
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: activeSection === 'authentication' ? 600 : 400
                                                    }}
                                                />
                                            )}
                                        </SidebarLink>
                                        
                                        <SidebarLink 
                                            active={activeSection === 'provisioning'} 
                                            onClick={() => scrollToSection('provisioning')}
                                        >
                                            <ListItemIcon sx={{ minWidth: sidebarVisible ? '40px' : '0px', m: sidebarVisible ? 0 : 'auto' }}>
                                                <mui.Tooltip title={!sidebarVisible ? "Access Provisioning" : ""}>
                                                    <PersonAddIcon color={activeSection === 'provisioning' ? 'primary' : 'inherit'} />
                                                </mui.Tooltip>
                                            </ListItemIcon>
                                            {sidebarVisible && (
                                                <ListItemText 
                                                    primary="Access Provisioning" 
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: activeSection === 'provisioning' ? 600 : 400
                                                    }}
                                                />
                                            )}
                                        </SidebarLink>
                                        
                                        <SidebarLink 
                                            active={activeSection === 'termination'} 
                                            onClick={() => scrollToSection('termination')}
                                        >
                                            <ListItemIcon sx={{ minWidth: sidebarVisible ? '40px' : '0px', m: sidebarVisible ? 0 : 'auto' }}>
                                                <mui.Tooltip title={!sidebarVisible ? "Access Termination" : ""}>
                                                    <PersonAddDisabledIcon color={activeSection === 'termination' ? 'primary' : 'inherit'} />
                                                </mui.Tooltip>
                                            </ListItemIcon>
                                            {sidebarVisible && (
                                                <ListItemText 
                                                    primary="Access Termination" 
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: activeSection === 'termination' ? 600 : 400
                                                    }}
                                                />
                                            )}
                                        </SidebarLink>
                                        
                                        <SidebarLink 
                                            active={activeSection === 'privileged'} 
                                            onClick={() => scrollToSection('privileged')}
                                        >
                                            <ListItemIcon sx={{ minWidth: sidebarVisible ? '40px' : '0px', m: sidebarVisible ? 0 : 'auto' }}>
                                                <mui.Tooltip title={!sidebarVisible ? "Privileged Accounts" : ""}>
                                                    <GroupsIcon color={activeSection === 'privileged' ? 'primary' : 'inherit'} />
                                                </mui.Tooltip>
                                            </ListItemIcon>
                                            {sidebarVisible && (
                                                <ListItemText 
                                                    primary="Privileged Accounts" 
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: activeSection === 'privileged' ? 600 : 400
                                                    }}
                                                />
                                            )}
                                        </SidebarLink>
                                        
                                        <SidebarLink 
                                            active={activeSection === 'access-review'} 
                                            onClick={() => scrollToSection('access-review')}
                                        >
                                            <ListItemIcon sx={{ minWidth: sidebarVisible ? '40px' : '0px', m: sidebarVisible ? 0 : 'auto' }}>
                                                <mui.Tooltip title={!sidebarVisible ? "User Access Review" : ""}>
                                                    <FindInPageIcon color={activeSection === 'access-review' ? 'primary' : 'inherit'} />
                                                </mui.Tooltip>
                                            </ListItemIcon>
                                            {sidebarVisible && (
                                                <ListItemText 
                                                    primary="User Access Review" 
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        fontWeight: activeSection === 'access-review' ? 600 : 400
                                                    }}
                                                />
                                            )}
                                        </SidebarLink>
                                    </List>
                                </MainPaper>
                            </mui.Grid>

                            <mui.Grid item xs={12} md={sidebarVisible ? 9 : 11}>
                                {loading ? (
                                    <mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="60vh">
                                        <mui.CircularProgress size={60} thickness={4} />
                                        <mui.Typography variant="h6" sx={{ mt: 3 }}>
                                            Loading Process Narrative...
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Retrieving application data and control information
                                        </mui.Typography>
                                    </mui.Box>
                                ) : error ? (
                                    <mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="60vh">
                                        <mui.Typography variant="h6" color="error" gutterBottom>
                                            {error}
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: "80%", textAlign: "center" }}>
                                            Unable to load the process narrative information. This could be due to a network issue or the application ID may not exist.
                                        </mui.Typography>
                                        <mui.Button 
                                            variant="contained" 
                                            color="primary"
                                            onClick={() => navigate('/processnarrative')}
                                            sx={{ mt: 2 }}
                                        >
                                            Back to Process Narratives
                                        </mui.Button>
                                    </mui.Box>
                                ) : !apps || !apps.APP_NAME ? (
                                    <mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="60vh">
                                        <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                                            No application data found for this ID.
                                        </mui.Typography>
                                        <mui.Button 
                                            variant="contained" 
                                            color="primary"
                                            onClick={() => navigate('/processnarrative')}
                                            sx={{ mt: 2 }}
                                        >
                                            Back to Process Narratives
                                        </mui.Button>
                                    </mui.Box>
                                ) : (
                                    <MainPaper>
                                        <mui.Box sx={{ p: { xs: 2, md: 4 } }} ref={narrativeRef}>
                                            {/* Authentication Section */}
                                            <SectionCard id="authentication">
                                                <SectionTitle variant="h6">
                                                    <SectionHeading
                                                        icon={<SecurityIcon sx={{ mr: 1.5 }} color="primary" />}
                                                        title="Authentication & Password Management"
                                                        sectionId="authentication"
                                                        isEditMode={isEditMode}
                                                        onSectionEdit={handleSectionEdit}
                                                    />
                                                </SectionTitle>
                                                
                                                {apps.AUTHENTICATION_TYPE ? (
                                                    <SectionContent>
                                                        <mui.Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                                                            <strong>{apps.APP_NAME}</strong>
                                                            {apps.AUTHENTICATION_TYPE === "Native/Direct" ? 
                                                                ' implements direct user authentication within the application itself, maintaining its own user credentials database rather than leveraging enterprise single sign-on (SSO). This authentication methodology requires users to maintain separate credentials specifically for this application. ' :
                                                                apps.AUTHENTICATION_TYPE === 'Single-Sign-On (SSO)' ? 
                                                                ' implements enterprise-wide single sign-on (SSO) authentication, allowing users to access the application using their centralized organization credentials. This eliminates the need for separate application-specific passwords and strengthens overall security through centralized identity management. ' :
                                                                apps.AUTHENTICATION_TYPE === 'Both Native and SSO' ? 
                                                                ' supports both native authentication and single sign-on (SSO), providing flexibility for different user types. While most enterprise users authenticate via SSO, certain scenarios may require direct application authentication for specialized accounts, external users, or administrative access. ' :
                                                                ' authentication documentation is not currently provided. Please update the general information section with the appropriate authentication details to complete this narrative.'}
                                                        </mui.Typography>
                                                        
                                                        <mui.Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                                                            <strong>Control Environment:</strong> <span style={{ fontWeight: 500 }}>{apps.COMPANY_NAME}</span>
                                                            {apps.PW_CONFIGURABLE === "Yes" ?
                                                                ` maintains direct control over the password security settings and authentication parameters for ${apps.APP_NAME}. This provides the organization with the ability to enforce consistent password policies aligned with internal security standards and compliance requirements.` :
                                                                ' does not have administrative capability to modify the password configuration as these settings are managed by the vendor or service provider. All authentication parameter changes must be coordinated through the vendor\'s established change management process.'}
                                                        </mui.Typography>

                                                        {apps.AUTHENTICATION_TYPE === 'Native/Direct' || apps.AUTHENTICATION_TYPE === 'Both Native and SSO' ? (
                                                            <InfoBlock>
                                                                <mui.Typography variant="subtitle2" sx={{ 
                                                                    fontWeight: 600, 
                                                                    display: 'flex', 
                                                                    alignItems: 'center',
                                                                    mb: 2
                                                                }}>
                                                                    <InfoIcon sx={{ mr: 1, color: '#4287f5' }} />
                                                                    Authentication Controls and Password Parameters
                                                                </mui.Typography>
                                                                
                                                                <mui.Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                                    The following password configuration has been implemented to enforce appropriate authentication security for users accessing the application:
                                                                </mui.Typography>
                                                                
                                                                <mui.Grid container spacing={2}>
                                                                    <mui.Grid item xs={12} md={6}>
                                                                        <mui.Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha('#e3f2fd', 0.5), borderRadius: 1 }}>
                                                                            <mui.Typography variant="body2" color="text.secondary" gutterBottom>Password Length</mui.Typography>
                                                                            <mui.Typography variant="body1" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                {pwdata.LENGTH ? 
                                                                                    <><mui.Box component="span" sx={{ color: '#1976d2', mr: 1 }}>{pwdata.LENGTH}</mui.Box> characters minimum</> : 
                                                                                    'Not configured'}
                                                                                <mui.Tooltip title="Strong passwords require sufficient length to resist brute force attacks">
                                                                                    <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', opacity: 0.6 }} />
                                                                                </mui.Tooltip>
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                        
                                                                        <mui.Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha('#e3f2fd', 0.5), borderRadius: 1 }}>
                                                                            <mui.Typography variant="body2" color="text.secondary" gutterBottom>Password History</mui.Typography>
                                                                            <mui.Typography variant="body1" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                {pwdata.HISTORY ? 
                                                                                    <><mui.Box component="span" sx={{ color: '#1976d2', mr: 1 }}>{pwdata.HISTORY}</mui.Box> previous passwords remembered</> : 
                                                                                    'Not configured'}
                                                                                <mui.Tooltip title="Prevents users from reusing recent passwords, enhancing security">
                                                                                    <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', opacity: 0.6 }} />
                                                                                </mui.Tooltip>
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Grid>
                                                                    
                                                                    <mui.Grid item xs={12} md={6}>
                                                                        <mui.Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha('#e3f2fd', 0.5), borderRadius: 1 }}>
                                                                            <mui.Typography variant="body2" color="text.secondary" gutterBottom>Password Expiry</mui.Typography>
                                                                            <mui.Typography variant="body1" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                {pwdata.AGE ? 
                                                                                    <><mui.Box component="span" sx={{ color: '#1976d2', mr: 1 }}>{pwdata.AGE}</mui.Box> days until required change</> : 
                                                                                    'Not configured'}
                                                                                <mui.Tooltip title="Regular password changes reduce the risk of compromised credentials">
                                                                                    <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', opacity: 0.6 }} />
                                                                                </mui.Tooltip>
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                        
                                                                        <mui.Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha('#e3f2fd', 0.5), borderRadius: 1 }}>
                                                                            <mui.Typography variant="body2" color="text.secondary" gutterBottom>Account Lockout</mui.Typography>
                                                                            <mui.Typography variant="body1" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                {pwdata.LOCKOUT_ATTEMPT ? 
                                                                                    <><mui.Box component="span" sx={{ color: '#1976d2', mr: 1 }}>{pwdata.LOCKOUT_ATTEMPT}</mui.Box> consecutive failed attempts</> : 
                                                                                    'Not configured'}
                                                                                <mui.Tooltip title="Prevents brute force attacks by temporarily locking accounts after multiple failed login attempts">
                                                                                    <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', opacity: 0.6 }} />
                                                                                </mui.Tooltip>
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Grid>
                                                                </mui.Grid>
                                                                
                                                                <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
                                                                    Password Complexity Requirements
                                                                </mui.Typography>
                                                                
                                                                <mui.Typography variant="body2" sx={{ mb: 2 }}>
                                                                    The following complexity requirements ensure that passwords are sufficiently robust against common attack vectors including dictionary attacks and credential stuffing:
                                                                </mui.Typography>
                                                                
                                                                <mui.Grid container spacing={2}>
                                                                    <mui.Grid item xs={6} sm={3}>
                                                                        <StatusChip 
                                                                            label="Special Characters" 
                                                                            status={pwdata.SPECIAL_CHAR === true ? 'enabled' : 'disabled'}
                                                                            icon={pwdata.SPECIAL_CHAR === true ? <CheckCircleIcon fontSize="small" /> : undefined}
                                                                        />
                                                                    </mui.Grid>
                                                                    
                                                                    <mui.Grid item xs={6} sm={3}>
                                                                        <StatusChip 
                                                                            label="Upper Case" 
                                                                            status={pwdata.UPPER === true ? 'enabled' : 'disabled'}
                                                                            icon={pwdata.UPPER === true ? <CheckCircleIcon fontSize="small" /> : undefined}
                                                                        />
                                                                    </mui.Grid>
                                                                    
                                                                    <mui.Grid item xs={6} sm={3}>
                                                                        <StatusChip 
                                                                            label="Lower Case" 
                                                                            status={pwdata.LOWER === true ? 'enabled' : 'disabled'}
                                                                            icon={pwdata.LOWER === true ? <CheckCircleIcon fontSize="small" /> : undefined}
                                                                        />
                                                                    </mui.Grid>
                                                                    
                                                                    <mui.Grid item xs={6} sm={3}>
                                                                        <StatusChip 
                                                                            label="Numbers" 
                                                                            status={pwdata.NUMBER === true ? 'enabled' : 'disabled'}
                                                                            icon={pwdata.NUMBER === true ? <CheckCircleIcon fontSize="small" /> : undefined}
                                                                        />
                                                                    </mui.Grid>
                                                                </mui.Grid>
                                                                
                                                                <mui.Box sx={{ mt: 3, p: 1.5, backgroundColor: alpha('#4caf50', 0.05), borderRadius: 1, border: `1px solid ${alpha('#4caf50', 0.1)}` }}>
                                                                    <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <CheckCircleIcon sx={{ mr: 1, mt: 0.2, color: '#4caf50', fontSize: 18 }} />
                                                                        These authentication controls collectively enforce the principle of strong credential management, a critical security control that helps prevent unauthorized access and protect sensitive data within {apps.APP_NAME}.
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            </InfoBlock>
                                                        ) : null}
                                                    </SectionContent>
                                                ) : (
                                                    <SectionContent>
                                                        <mui.Alert severity="info" sx={{ mb: 2 }}>
                                                            <mui.AlertTitle>User Access Review Information Missing</mui.AlertTitle>
                                                            There is no available user access review process documentation for {apps.APP_NAME}. Implementing and documenting a regular access review process is crucial for maintaining proper access controls and meeting compliance requirements.
                                                        </mui.Alert>
                                                        
                                                        <mui.Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={() => navigate(`/applications/setupinfo/${id}`)}
                                                            startIcon={<SettingsIcon />}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Define User Access Review Process
                                                        </mui.Button>
                                                    </SectionContent>
                                                )}
                                            </SectionCard>

                                            {/* Provisioning Section */}
                                            <SectionCard id="provisioning">
                                                <SectionTitle variant="h6">
                                                    <SectionHeading
                                                        icon={<PersonAddIcon sx={{ mr: 1.5 }} color="primary" />}
                                                        title="Access Provisioning Process"
                                                        sectionId="provisioning"
                                                        isEditMode={isEditMode}
                                                        onSectionEdit={handleSectionEdit}
                                                    />
                                                </SectionTitle>
                                                
                                                {provisioningData?.FORM ? (
                                                    <SectionContent>
                                                        <mui.Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                                                            <strong>Control Objective:</strong> {apps.COMPANY_NAME} has established a formalized access provisioning process for {apps.APP_NAME} to ensure that user access is properly requested, documented, approved, and granted according to the principle of least privilege. This process helps maintain segregation of duties and ensures that users receive only the access rights necessary for their job responsibilities.
                                                        </mui.Typography>

                                                        <mui.Box sx={{ 
                                                            mt: 3, 
                                                            mb: 4, 
                                                            p: 2.5, 
                                                            borderRadius: 2,
                                                            backgroundColor: alpha('#f5f5f5', 0.7),
                                                            border: '1px solid #e0e0e0'
                                                        }}>
                                                            <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#0d47a1' }}>
                                                                Access Request Documentation and Approval Workflow
                                                            </mui.Typography>
                                                            
                                                            <mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <mui.Box sx={{ 
                                                                            minWidth: 28, 
                                                                            height: 28, 
                                                                            borderRadius: '50%', 
                                                                            backgroundColor: '#1976d2',
                                                                            color: 'white',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            mr: 2,
                                                                            mt: 0.5,
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                        >
                                                                            1
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body1" fontWeight={500} gutterBottom>
                                                                                Request Documentation
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                                Users submit formal access requests through {provisioningData.FORM.replace(",", " and ")}. These requests include specific roles, permissions, and access levels required for their job functions, establishing a clear audit trail of all access requests.
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                </mui.Box>
                                                                
                                                                <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <mui.Box 
                                                                        sx={{ 
                                                                            minWidth: 28, 
                                                                            height: 28, 
                                                                            borderRadius: '50%', 
                                                                            backgroundColor: '#1976d2',
                                                                            color: 'white',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            mr: 2,
                                                                            mt: 0.5,
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        2
                                                                    </mui.Box>
                                                                    <mui.Box>
                                                                        <mui.Typography variant="body1" fontWeight={500} gutterBottom>
                                                                            Approval Process
                                                                        </mui.Typography>
                                                                        <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                            All access requests require formal approval from {provisioningData.APPROVERS.replace(",", " and ")} before implementation. This approval serves as an authorization control point to verify the appropriateness of requested access rights and prevent unauthorized provisioning.
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                </mui.Box>
                                                                
                                                                <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <mui.Box 
                                                                        sx={{ 
                                                                            minWidth: 28, 
                                                                            height: 28, 
                                                                            borderRadius: '50%', 
                                                                            backgroundColor: '#1976d2',
                                                                            color: 'white',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            mr: 2,
                                                                            mt: 0.5,
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        3
                                                                    </mui.Box>
                                                                    <mui.Box>
                                                                        <mui.Typography variant="body1" fontWeight={500} gutterBottom>
                                                                            Access Implementation
                                                                        </mui.Typography>
                                                                        <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                            After approval is obtained, {provisioningData.GRANTOR ? provisioningData.GRANTOR.replace(",", " and ") : '(Grantor not set)'} provisions the requested access in {apps.APP_NAME}. This segregation between approvers and implementers establishes an appropriate division of responsibilities, reducing the risk of unauthorized access.
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                </mui.Box>
                                                            </mui.Box>
                                                        </mui.Box>

                                                        <mui.Box sx={{ mt: 4 }}>
                                                            <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#0d47a1' }}>
                                                                Provisioning Process for Different Worker Types
                                                            </mui.Typography>
                                                            
                                                            {provisioningData.PROCESS_DIFFERENCE === "Yes" ? (
                                                                <mui.Box sx={{ backgroundColor: alpha('#ffb74d', 0.08), p: 2.5, borderRadius: 2, border: `1px solid ${alpha('#ffb74d', 0.2)}` }}>
                                                                    <mui.Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                                                                        <strong>{apps.COMPANY_NAME}</strong> maintains differentiated provisioning processes for regular employees versus temporary or contract-based workers to address their distinct risk profiles and access needs:
                                                                    </mui.Typography>
                                                                    
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                                        <mui.Box sx={{ 
                                                                            p: 1, 
                                                                            backgroundColor: 'white', 
                                                                            borderRadius: 1, 
                                                                            mr: 2,
                                                                            color: '#ed6c02',
                                                                            display: 'flex'
                                                                        }}>
                                                                            <PersonAddIcon fontSize="small" />
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body2" fontWeight={500}>
                                                                                Regular Employee Process
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary">
                                                                                The standard access provisioning process described above.
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                    
                                                                    <mui.Divider sx={{ my: 2 }} />
                                                                    
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <mui.Box sx={{ 
                                                                            p: 1, 
                                                                            backgroundColor: 'white', 
                                                                            borderRadius: 1, 
                                                                            mr: 2,
                                                                            color: '#ed6c02',
                                                                            display: 'flex'
                                                                        }}>
                                                                            <mui.SvgIcon fontSize="small">
                                                                                <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
                                                                            </mui.SvgIcon>
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body2" fontWeight={500}>
                                                                                Temporary/Contract Worker Process
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1, p: 1, backgroundColor: alpha('#fff', 0.5), borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)' }}>
                                                                                "{provisioningData.PROCESS_DIFFERENCE_OTHER}"
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                </mui.Box>
                                                            ) : (
                                                                <mui.Box sx={{ backgroundColor: alpha('#e3f2fd', 0.5), p: 2.5, borderRadius: 2, border: '1px solid #bbdefb' }}>
                                                                    <mui.Typography variant="body2" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'flex-start' }}>
                                                                        <InfoIcon sx={{ mr: 1.5, mt: 0.2, color: '#1976d2', fontSize: 18 }} />
                                                                        The access provisioning process described above applies uniformly to both regular employees and temporary/contract-based workers. There is no differentiation in how access is requested, approved, or granted within {apps.APP_NAME}, ensuring consistent control implementation across all worker types.
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            )}
                                                        </mui.Box>
                                                        
                                                        <mui.Box sx={{ mt: 3, p: 2, backgroundColor: alpha('#4caf50', 0.05), borderRadius: 1, border: `1px solid ${alpha('#4caf50', 0.1)}` }}>
                                                            <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', fontStyle: 'italic' }}>
                                                                <CheckCircleIcon sx={{ mr: 1, mt: 0.2, color: '#4caf50', fontSize: 18 }} />
                                                                This provisioning process provides a comprehensive control framework for managing user access to {apps.APP_NAME}, ensuring appropriate authorization, documentation, and implementation of access rights in accordance with business needs and security requirements.
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    </SectionContent>
                                                ) : (
                                                    <SectionContent>
                                                        <mui.Alert severity="info" sx={{ mb: 2 }}>
                                                            <mui.AlertTitle>Access Provisioning Information Missing</mui.AlertTitle>
                                                            There is no available access request documentation or approval process for {apps.APP_NAME} at the moment. Documenting the provisioning process is essential for demonstrating effective access controls during audits.
                                                        </mui.Alert>
                                                        
                                                        <mui.Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={() => navigate(`/applications/controls/${id}?section=provisioning`)}
                                                            startIcon={<SettingsIcon />}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Define Provisioning Process
                                                        </mui.Button>
                                                    </SectionContent>
                                                )}
                                            </SectionCard>

                                            {/* Termination Section */}
                                            <SectionCard id="termination">
                                                <SectionTitle variant="h6">
                                                    <SectionHeading
                                                        icon={<PersonAddDisabledIcon sx={{ mr: 1.5 }} color="primary" />}
                                                        title="Access Termination Controls"
                                                        sectionId="termination"
                                                        isEditMode={isEditMode}
                                                        onSectionEdit={handleSectionEdit}
                                                    />
                                                </SectionTitle>
                                                
                                                {terminationData?.NETWORK_RELIANCE ? (
                                                    <SectionContent>
                                                        <mui.Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                                                            <strong>Control Objective:</strong> {apps.COMPANY_NAME} has established a formalized access termination process for {apps.APP_NAME} to ensure that user access is promptly revoked when no longer required. This process helps prevent unauthorized access by former employees or users whose responsibilities have changed, protecting the confidentiality and integrity of system data.
                                                        </mui.Typography>
                                                        
                                                        <mui.Box sx={{ 
                                                            p: 2.5, 
                                                            borderRadius: 2,
                                                            mb: 4,
                                                            backgroundColor: alpha(
                                                                terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' ? '#e8f5e9' : 
                                                                terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' ? '#fff8e1' : 
                                                                '#e3f2fd', 0.5),
                                                            border: `1px solid ${alpha(
                                                                terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' ? '#4caf50' : 
                                                                terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' ? '#ffb74d' : 
                                                                '#2196f3', 0.2)}`
                                                        }}>
                                                            <mui.Typography variant="subtitle1" fontWeight={600} sx={{ 
                                                                mb: 2,
                                                                color: terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' ? '#2e7d32' : 
                                                                    terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' ? '#ed6c02' : 
                                                                    '#0d47a1',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}>
                                                                <mui.SvgIcon fontSize="small" sx={{ mr: 1 }}>
                                                                    {terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' ? 
                                                                        <path d="M6,19A5,5 0 0,1 1,14C1,11.3 2.73,9.1 5.08,8.28C5.03,8 5,7.71 5,7.42C5,4.42 7.46,2 10.5,2C13.54,2 16,4.42 16,7.42C16,7.71 15.97,8 15.92,8.28C18.27,9.1 20,11.3 20,14A5,5 0 0,1 15,19H6M8.5,15L12,11.5L15.5,15H8.5Z" /> : 
                                                                        terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' ? 
                                                                            <path d="M6,19A5,5 0 0,1 1,14C1,11.3 2.73,9.1 5.08,8.28C5.03,8 5,7.71 5,7.42C5,4.42 7.46,2 10.5,2C13.54,2 16,4.42 16,7.42C16,7.71 15.97,8 15.92,8.28C18.27,9.1 20,11.3 20,14A5,5 0 0,1 15,19H6M13.5,15H11.5V9.5H9.5V15H7.5L10.5,18L13.5,15Z" /> : 
                                                                            <path d="M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16Z" />
                                                                    }
                                                                </mui.SvgIcon>
                                                                Network Reliance Model: {
                                                                    terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' ? 'Full Network Dependency' : 
                                                                    terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' ? 'Hybrid Approach' : 
                                                                    'Application-Layer Termination'
                                                                }
                                                            </mui.Typography>

                                                            <mui.Box sx={{ mb: 2 }}>
                                                                {terminationData.NETWORK_RELIANCE === 'Yes - Full Reliance' && (
                                                                    <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                        <strong>{apps.COMPANY_NAME}</strong> employs a <strong>network-dependent termination strategy</strong> for {apps.APP_NAME}. When a user's network access is revoked through the organization's centralized identity management system, the user automatically loses all access capabilities to this application. This approach leverages the network as the primary access control point, creating an efficient and consistent termination process.
                                                                        
                                                                        {network?.APP_NAME && (
                                                                            <mui.Box sx={{ mt: 2, p: 1.5, backgroundColor: 'white', borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)' }}>
                                                                                <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                    <mui.SvgIcon fontSize="small" sx={{ mr: 1, color: '#4caf50' }}>
                                                                                        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
                                                                                    </mui.SvgIcon>
                                                                                    Access termination is controlled through the <strong>{network?.APP_NAME}</strong> network system. Refer to the detailed network termination documentation for comprehensive process information.
                                                                                </mui.Typography>
                                                                            </mui.Box>
                                                                        )}
                                                                    </mui.Typography>
                                                                )}

                                                                {terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' && (
                                                                    <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                        <strong>{apps.COMPANY_NAME}</strong> employs a <strong>hybrid termination approach</strong> for {apps.APP_NAME}. For most internal users, terminating network access effectively prevents application access. However, because some users (such as external partners, vendors, or specialized accounts) can authenticate directly to the application without using the corporate network, a dual-layer termination process is required.
                                                                    </mui.Typography>
                                                                )}
                                                                
                                                                {terminationData.NETWORK_RELIANCE === 'No - Not Relying' && (
                                                                    <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                        <strong>{apps.COMPANY_NAME}</strong> employs an <strong>application-specific termination process</strong> for {apps.APP_NAME}, independent of network access controls. All user access terminations must be explicitly executed within the application itself, as network-level termination does not affect application access rights.
                                                                    </mui.Typography>
                                                                )}
                                                            </mui.Box>

                                                            {(terminationData.NETWORK_RELIANCE === 'Yes - Partial Reliance' || terminationData.NETWORK_RELIANCE === 'No - Not Relying') && (
                                                                <mui.Box sx={{ mt: 3, mb: 4 }}>
                                                                    <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#0d47a1' }}>
                                                                        Application-Layer Termination Process
                                                                    </mui.Typography>
                                                                    
                                                                    <InfoBlock>
                                                                        <mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                                                            <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                                <mui.Box 
                                                                                    sx={{ 
                                                                                        minWidth: 28, 
                                                                                        height: 28, 
                                                                                        borderRadius: '50%', 
                                                                                        backgroundColor: '#1976d2',
                                                                                        color: 'white',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        mr: 2,
                                                                                        mt: 0.2,
                                                                                        fontWeight: 'bold'
                                                                                    }}
                                                                                >
                                                                                    1
                                                                                </mui.Box>
                                                                                <mui.Box>
                                                                                    <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                        Termination Notification
                                                                                    </mui.Typography>
                                                                                    <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                                        When an employee or contractor leaves the organization or changes roles, <strong>{terminationData?.TERM_NOTIFYER?.replace(",", " and ")}</strong> notifies the system administrator via <strong>{terminationData?.TERM_DOCUMENTATION?.replace(",", " and ")}</strong>, providing all necessary details about the terminated user.
                                                                                    </mui.Typography>
                                                                                </mui.Box>
                                                                            </mui.Box>
                                                                            
                                                                            <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                                <mui.Box 
                                                                                    sx={{ 
                                                                                        minWidth: 28, 
                                                                                        height: 28, 
                                                                                        borderRadius: '50%', 
                                                                                        backgroundColor: '#1976d2',
                                                                                        color: 'white',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        mr: 2,
                                                                                        mt: 0.2,
                                                                                        fontWeight: 'bold'
                                                                                    }}
                                                                                >
                                                                                    2
                                                                                </mui.Box>
                                                                                <mui.Box>
                                                                                    <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                        Access Revocation
                                                                                    </mui.Typography>
                                                                                    <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                                        Upon receiving termination notification, the system administrator {terminationData?.DISABLE_TYPE?.replace(",", " and ")} the user's account 
                                                                                        {terminationData?.TERM_PROCESS === "Manual"
                                                                                            ? ' through manual intervention within the application interface,'
                                                                                            : terminationData?.TERM_PROCESS === "Automated"
                                                                                                ? ' through an automated workflow process,'
                                                                                                : terminationData?.TERM_PROCESS
                                                                                                    ? ` via the ${terminationData?.TERM_PROCESS.replace(",", " and ")} process,`
                                                                                                    : ' using standard procedures,'}
                                                                                     preventing any further system access by that user.
                                                                                    </mui.Typography>
                                                                                </mui.Box>
                                                                            </mui.Box>
                                                                            
                                                                            <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                                <mui.Box 
                                                                                    sx={{ 
                                                                                        minWidth: 28, 
                                                                                        height: 28, 
                                                                                        borderRadius: '50%', 
                                                                                        backgroundColor: '#1976d2',
                                                                                        color: 'white',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        mr: 2,
                                                                                        mt: 0.2,
                                                                                        fontWeight: 'bold'
                                                                                    }}
                                                                                >
                                                                                    3
                                                                                </mui.Box>
                                                                                <mui.Box>
                                                                                    <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                        Termination Verification
                                                                                    </mui.Typography>
                                                                                    <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                                        After performing the access revocation, the administrator documents the completed termination action for audit trail purposes, ensuring a complete record of user access lifecycle.
                                                                                    </mui.Typography>
                                                                                </mui.Box>
                                                                            </mui.Box>
                                                                        </mui.Box>
                                                                        
                                                                        <mui.Box sx={{ 
                                                                            display: 'flex', 
                                                                            alignItems: 'center', 
                                                                            mt: 3,
                                                                            p: 1.5,
                                                                            borderRadius: 1,
                                                                            backgroundColor: alpha('#2196f3', 0.1),
                                                                            border: `1px solid ${alpha('#2196f3', 0.2)}`
                                                                        }}>
                                                                            <mui.SvgIcon fontSize="small" sx={{ mr: 1.5, color: '#1976d2' }}>
                                                                                <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                                                                            </mui.SvgIcon>
                                                                            <mui.Typography variant="body2" fontWeight={500}>
                                                                                Timeliness Requirement: <strong>{terminationData.TIMELINESS} days</strong> from the user's last working date
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </InfoBlock>
                                                                </mui.Box>
                                                            )}

                                                            <mui.Box sx={{ mt: 4 }}>
                                                                <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#0d47a1' }}>
                                                                    Differentiated Termination Processes
                                                                </mui.Typography>
                                                                
                                                                {terminationData.PROCESS_DIFFERENCE === "Yes" ? (
                                                                    <mui.Box sx={{ backgroundColor: alpha('#ffb74d', 0.08), p: 2.5, borderRadius: 2, border: `1px solid ${alpha('#ffb74d', 0.2)}` }}>
                                                                        <mui.Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                                                                            <strong>{apps.COMPANY_NAME}</strong> applies different termination procedures for regular employees versus temporary or contract-based workers due to their distinct employment relationships and risk profiles:
                                                                        </mui.Typography>
                                                                        
                                                                        <mui.Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                                            <mui.Box sx={{ 
                                                                                p: 1, 
                                                                                backgroundColor: 'white', 
                                                                                borderRadius: 1, 
                                                                                mr: 2,
                                                                                color: '#ed6c02',
                                                                                display: 'flex'
                                                                            }}>
                                                                                <PersonAddDisabledIcon fontSize="small" />
                                                                            </mui.Box>
                                                                            <mui.Box>
                                                                                <mui.Typography variant="body2" fontWeight={500}>
                                                                                    Regular Employee Termination
                                                                                </mui.Typography>
                                                                                <mui.Typography variant="body2" color="text.secondary">
                                                                                    Follows the standard termination process described above.
                                                                                </mui.Typography>
                                                                            </mui.Box>
                                                                        </mui.Box>
                                                                        
                                                                        <mui.Divider sx={{ my: 2 }} />
                                                                        
                                                                        <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                            <mui.Box sx={{ 
                                                                                p: 1, 
                                                                                backgroundColor: 'white', 
                                                                                borderRadius: 1, 
                                                                                mr: 2,
                                                                                color: '#ed6c02',
                                                                                display: 'flex'
                                                                            }}>
                                                                                <mui.SvgIcon fontSize="small">
                                                                                    <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
                                                                                </mui.SvgIcon>
                                                                            </mui.Box>
                                                                            <mui.Box>
                                                                                <mui.Typography variant="body2" fontWeight={500}>
                                                                                    Temporary/Contract Worker Termination
                                                                                </mui.Typography>
                                                                                <mui.Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1, p: 1, backgroundColor: alpha('#fff', 0.5), borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)' }}>
                                                                                    "{terminationData.PROCESS_DIFFERENCE_OTHER}"
                                                                                </mui.Typography>
                                                                            </mui.Box>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                ) : (
                                                                    <mui.Box sx={{ backgroundColor: alpha('#e3f2fd', 0.5), p: 2.5, borderRadius: 2, border: '1px solid #bbdefb' }}>
                                                                        <mui.Typography variant="body2" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'flex-start' }}>
                                                                            <InfoIcon sx={{ mr: 1.5, mt: 0.2, color: '#1976d2', fontSize: 18 }} />
                                                                            The termination process described above applies consistently to both regular employees and temporary/contract-based workers. There is no differentiation in how access is revoked within {apps.APP_NAME}, ensuring uniform control implementation across all worker types.
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                )}
                                                            </mui.Box>
                                                        </mui.Box>
                                                        
                                                        <mui.Box sx={{ mt: 3, p: 2, backgroundColor: alpha('#4caf50', 0.05), borderRadius: 1, border: `1px solid ${alpha('#4caf50', 0.1)}` }}>
                                                            <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', fontStyle: 'italic' }}>
                                                                <CheckCircleIcon sx={{ mr: 1, mt: 0.2, color: '#4caf50', fontSize: 18 }} />
                                                                This termination process provides a comprehensive control framework for managing user access removal from {apps.APP_NAME}, ensuring timely revocation of access rights to maintain system security and prevent unauthorized access after employment changes.
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    </SectionContent>
                                                ) : (
                                                    <SectionContent>
                                                        <mui.Alert severity="info" sx={{ mb: 2 }}>
                                                            <mui.AlertTitle>Access Termination Information Missing</mui.AlertTitle>
                                                            There is no available access termination process documentation for {apps.APP_NAME}. Implementing and documenting a termination process is crucial for preventing unauthorized access by former employees.
                                                        </mui.Alert>
                                                        
                                                        <mui.Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={() => navigate(`/applications/controls/${id}?section=termination`)}
                                                            startIcon={<SettingsIcon />}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Define Termination Process
                                                        </mui.Button>
                                                    </SectionContent>
                                                )}
                                            </SectionCard>

                                            {/* Privileged Accounts Section */}
                                            <SectionCard id="privileged">
                                                <SectionTitle variant="h6">
                                                    <SectionHeading
                                                        icon={<GroupsIcon sx={{ mr: 1.5 }} color="primary" />}
                                                        title="Privileged Accounts Management"
                                                        sectionId="privileged"
                                                        isEditMode={isEditMode}
                                                        onSectionEdit={handleSectionEdit}
                                                    />
                                                </SectionTitle>
                                                
                                                {privilegedData && Object.keys(privilegedData).length > 0 ? (
                                                    <SectionContent>
                                                        <mui.Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                                                            <strong>Control Objective:</strong> {apps.COMPANY_NAME} has established specialized controls for managing privileged/administrative accounts within {apps.APP_NAME}. These controls ensure that elevated access privileges are strictly governed, monitored, and reviewed to reduce the risk of unauthorized system changes or access to sensitive data.
                                                        </mui.Typography>
                                                        
                                                        <mui.Box sx={{ 
                                                            p: 2.5, 
                                                            borderRadius: 2,
                                                            mb: 4,
                                                            backgroundColor: alpha('#e3f2fd', 0.5),
                                                            border: `1px solid ${alpha('#2196f3', 0.2)}`
                                                        }}>
                                                            <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#0d47a1', display: 'flex', alignItems: 'center' }}>
                                                                <SecurityIcon sx={{ mr: 1.5 }} fontSize="small" />
                                                                Administrative Roles and Capabilities
                                                            </mui.Typography>

                                                            <mui.Box sx={{ mb: 3 }}>
                                                                <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                    <strong>Administrative Roles:</strong> {privilegedData.ADMIN_ROLES || "Not specified"}
                                                                </mui.Typography>
                                                                
                                                                {privilegedData.CAPABILITIES && (
                                                                    <mui.Box sx={{ mt: 2 }}>
                                                                        <mui.Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                                                            Key Administrative Capabilities:
                                                                        </mui.Typography>
                                                                        <mui.Box sx={{ pl: 2 }}>
                                                                            <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                                {privilegedData.CAPABILITIES}
                                                                                {privilegedData.CAPABILITIES_OTHER && (
                                                                                    <mui.Box sx={{ mt: 1, p: 1, backgroundColor: alpha('#fff', 0.5), borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)', fontStyle: 'italic' }}>
                                                                                        {privilegedData.CAPABILITIES_OTHER}
                                                                                    </mui.Box>
                                                                                )}
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                )}
                                                            </mui.Box>

                                                            <mui.Divider sx={{ my: 2 }} />

                                                            <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 3, color: '#0d47a1', display: 'flex', alignItems: 'center' }}>
                                                                <AssignmentIcon sx={{ mr: 1.5 }} fontSize="small" />
                                                                Privileged Access Review Process
                                                            </mui.Typography>

                                                            <mui.Box sx={{ mb: 3 }}>
                                                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                    <mui.Chip 
                                                                        label={privilegedData.ADMIN_REVIEW_PERFORMED === "Yes" ? "Regular Reviews Performed" : "No Regular Reviews"} 
                                                                        color={privilegedData.ADMIN_REVIEW_PERFORMED === "Yes" ? "success" : "warning"}
                                                                        size="small"
                                                                        sx={{ mr: 2 }}
                                                                    />
                                                                </mui.Box>
                                                                
                                                                {privilegedData.ADMIN_REVIEW_PERFORMED === "Yes" && (
                                                                    <>
                                                                        <mui.Typography variant="body2" sx={{ lineHeight: 1.7, mt: 2 }}>
                                                                            <strong>Review Frequency:</strong> {privilegedData.ADMIN_REVIEW_FREQUENCY || "Not specified"}
                                                                        </mui.Typography>
                                                                        
                                                                        <mui.Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
                                                                            <strong>Documentation Method:</strong> {privilegedData.ADMIN_REVIEW_DOCUMENT || "Not specified"}
                                                                            {privilegedData.ADMIN_REVIEW_DOCUMENT_OTHER && (
                                                                                <mui.Box sx={{ mt: 1, p: 1, backgroundColor: alpha('#fff', 0.5), borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)', fontStyle: 'italic' }}>
                                                                                    {privilegedData.ADMIN_REVIEW_DOCUMENT_OTHER}
                                                                                </mui.Box>
                                                                            )}
                                                                        </mui.Typography>
                                                                    </>
                                                                )}
                                                                
                                                                {privilegedData.ADMIN_REVIEW_PERFORMED !== "Yes" && (
                                                                    <mui.Box sx={{ backgroundColor: alpha('#fff3e0', 0.5), p: 2, borderRadius: 1, border: '1px solid #ffe0b2', mt: 2 }}>
                                                                        <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                            <InfoIcon sx={{ mr: 1.5, mt: 0.2, color: '#f57c00', fontSize: 18 }} />
                                                                            No formal privileged account review process is currently implemented for {apps.APP_NAME}. Establishing regular reviews of privileged access is recommended to mitigate the risk of unauthorized system changes and maintain proper access control.
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                )}
                                                            </mui.Box>
                                                        </mui.Box>
                                                        
                                                        <mui.Box sx={{ mt: 3, p: 2, backgroundColor: alpha('#4caf50', 0.05), borderRadius: 1, border: `1px solid ${alpha('#4caf50', 0.1)}` }}>
                                                            <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', fontStyle: 'italic' }}>
                                                                <CheckCircleIcon sx={{ mr: 1, mt: 0.2, color: '#4caf50', fontSize: 18 }} />
                                                                This privileged account management process provides a framework for controlling and monitoring administrative access to {apps.APP_NAME}, helping to ensure that powerful system capabilities are properly governed and secured.
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    </SectionContent>
                                                ) : (
                                                    <SectionContent>
                                                        <mui.Alert severity="info" sx={{ mb: 2 }}>
                                                            <mui.AlertTitle>Privileged Account Information Missing</mui.AlertTitle>
                                                            There is no available privileged account management documentation for {apps.APP_NAME}. Documenting the privileged access controls is essential for managing elevated access rights effectively.
                                                        </mui.Alert>
                                                        
                                                        <mui.Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={() => navigate(`/applications/controls/${id}?section=admin`)}
                                                            startIcon={<SettingsIcon />}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Define Privileged Account Controls
                                                        </mui.Button>
                                                    </SectionContent>
                                                )}
                                            </SectionCard>

                                            {/* UAR Section */}
                                            <SectionCard id="access-review">
                                                <SectionTitle variant="h6">
                                                    <SectionHeading
                                                        icon={<FindInPageIcon sx={{ mr: 1.5 }} color="primary" />}
                                                        title="User Access Review"
                                                        sectionId="access-review"
                                                        isEditMode={isEditMode}
                                                        onSectionEdit={handleSectionEdit}
                                                    />
                                                </SectionTitle>
                                                
                                                {/* Check if uarData exists and has content */}
                                                {(uarData && Object.keys(uarData).length > 0) ? (
                                                    <SectionContent>
                                                        <mui.Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                                                            <strong>Control Objective:</strong> {apps.COMPANY_NAME} performs periodic user access reviews for {apps.APP_NAME} to ensure that access rights remain appropriate over time. This process helps identify and remediate inappropriate access, reducing the risk of unauthorized actions within the application.
                                                        </mui.Typography>

                                                        <SectionSubtitle>Review Methodology</SectionSubtitle>
                                                        
                                                        <mui.Box 
                                                            sx={{ 
                                                                p: 2, 
                                                                mt: 1, 
                                                                mb: 3, 
                                                                borderRadius: 2,
                                                                backgroundColor: 'rgba(245, 245, 245, 0.5)',
                                                                border: '1px solid rgba(0, 0, 0, 0.05)'
                                                            }}
                                                        >
                                                            <mui.Grid container spacing={3}>
                                                                <mui.Grid item xs={12} md={4}>
                                                                    <mui.Box sx={{ textAlign: 'center', py: 1 }}>
                                                                        <CalendarMonthIcon 
                                                                            color="primary"
                                                                            sx={{ fontSize: 42, mb: 1, opacity: 0.8 }}
                                                                        />
                                                                        <mui.Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                            Review Frequency
                                                                        </mui.Typography>
                                                                        <mui.Typography variant="body1" fontWeight={500}>
                                                                            {uarData.FREQUENCY || "Not specified"}
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                </mui.Grid>
                                                                
                                                                <mui.Grid item xs={12} md={4}>
                                                                    <mui.Box sx={{ textAlign: 'center', py: 1 }}>
                                                                        <PersonSearchIcon 
                                                                            color="primary"
                                                                            sx={{ fontSize: 42, mb: 1, opacity: 0.8 }}
                                                                        />
                                                                        <mui.Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                            Review Scope
                                                                        </mui.Typography>
                                                                        <mui.Typography variant="body1" fontWeight={500}>
                                                                            {uarData.SCOPE_USERS || "All users"}
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                </mui.Grid>
                                                                
                                                                <mui.Grid item xs={12} md={4}>
                                                                    <mui.Box sx={{ textAlign: 'center', py: 1 }}>
                                                                        <VerifiedUserIcon 
                                                                            color="primary"
                                                                            sx={{ fontSize: 42, mb: 1, opacity: 0.8 }}
                                                                        />
                                                                        <mui.Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                            Review Approach
                                                                        </mui.Typography>
                                                                        <mui.Typography variant="body1" fontWeight={500}>
                                                                            {uarData.IS_TOOL === "Yes" ? "Tool-based Review" : "Manual Review"}
                                                                        </mui.Typography>
                                                                    </mui.Box>
                                                                </mui.Grid>
                                                            </mui.Grid>
                                                        </mui.Box>
                                                        
                                                        <mui.Box sx={{ mb: 4 }}>
                                                            <SectionSubtitle>Review Process</SectionSubtitle>
                                                            
                                                            <mui.Box sx={{ 
                                                                p: 2.5, 
                                                                borderLeft: '4px solid #3f51b5', 
                                                                backgroundColor: alpha('#3f51b5', 0.03),
                                                                borderRadius: '0 8px 8px 0',
                                                                mt: 2
                                                            }}>
                                                                <mui.Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                                    <strong>Review Initiator:</strong> {uarData.EXTRACTOR || "System Administrator"} <br />
                                                                    <strong>Review Performer:</strong> {uarData.REVIEWER || "System Owner"} <br />
                                                                    <strong>Documentation Method:</strong> {uarData.EXTRACTION_SEND?.replace(",", " and ") || "Electronic Records"}
                                                                    {uarData.IS_TOOL === "Yes" && uarData.TOOL_NAME && (
                                                                        <><br /><strong>Review Tool:</strong> {uarData.TOOL_NAME}</>
                                                                    )}
                                                                </mui.Typography>
                                                            </mui.Box>
                                                        </mui.Box>
                                                        
                                                        <mui.Box sx={{ mb: 3 }}>
                                                            <SectionSubtitle>User Access Review Procedure</SectionSubtitle>
                                                            
                                                            <InfoBlock>
                                                                <mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <mui.Box 
                                                                            sx={{ 
                                                                                minWidth: 28, 
                                                                                height: 28, 
                                                                                borderRadius: '50%', 
                                                                                backgroundColor: '#3f51b5',
                                                                                color: 'white',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                mr: 2,
                                                                                mt: 0.2,
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            1
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                Review Preparation
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                                                                                The {uarData.EXTRACTOR || "System Administrator"} retrieves a complete list of active users and their assigned permissions/roles from {apps.APP_NAME}. This list is prepared for review according to the established schedule ({uarData.FREQUENCY || "scheduled intervals"}).
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                    
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <mui.Box 
                                                                            sx={{ 
                                                                                minWidth: 28, 
                                                                                height: 28, 
                                                                                borderRadius: '50%', 
                                                                                backgroundColor: '#3f51b5',
                                                                                color: 'white',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                mr: 2,
                                                                                mt: 0.2,
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            2
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                Review Execution
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                                                                                The {uarData.REVIEWER || "System Owner"} reviews the user access list to verify:
                                                                                <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                                                                                    <li>Users still require access based on job responsibilities</li>
                                                                                    <li>Users' permission levels are appropriate for their role</li>
                                                                                    <li>Any terminated employees or transferred staff have been removed</li>
                                                                                    <li>Any unused accounts are identified for potential deactivation</li>
                                                                                </ul>
                                                                                {uarData.SCOPE_ROLES && (
                                                                                    <strong>Roles in scope: </strong> 
                                                                                )}{uarData.SCOPE_ROLES}
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                    
                                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <mui.Box 
                                                                            sx={{ 
                                                                                minWidth: 28, 
                                                                                height: 28, 
                                                                                borderRadius: '50%', 
                                                                                backgroundColor: '#3f51b5',
                                                                                color: 'white',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                mr: 2,
                                                                                mt: 0.2,
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            3
                                                                        </mui.Box>
                                                                        <mui.Box>
                                                                            <mui.Typography variant="body2" fontWeight={500} gutterBottom>
                                                                                Review Remediation & Documentation
                                                                            </mui.Typography>
                                                                            <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                                                                                Based on the review findings, necessary access changes are implemented promptly. The review completion is documented using {uarData.CHANGE_DOCUMENTATION?.replace(",", " and ") || "electronic records"}, including:
                                                                                <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                                                                                    <li>Date of review completion</li>
                                                                                    <li>Name of reviewer(s)</li>
                                                                                    <li>Summary of findings</li>
                                                                                    <li>Actions taken to remediate inappropriate access</li>
                                                                                    <li>Attestation of review completion</li>
                                                                                </ul>
                                                                                {uarData.DAYS_TO_COMPLETE && (
                                                                                    <strong>Days to complete remediation: {uarData.DAYS_TO_COMPLETE}</strong>
                                                                                )}
                                                                            </mui.Typography>
                                                                        </mui.Box>
                                                                    </mui.Box>
                                                                </mui.Box>
                                                            </InfoBlock>
                                                        </mui.Box>
                                                        
                                                        {uarData.SOD_CHECK && (
                                                            <mui.Box sx={{ mb: 3 }}>
                                                                <SectionSubtitle>Segregation of Duties</SectionSubtitle>
                                                                
                                                                <mui.Box sx={{ p: 2, backgroundColor: alpha('#e8f5e9', 0.5), borderRadius: 2, border: '1px solid #c8e6c9', mb: 3 }}>
                                                                    <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <CheckCircleIcon sx={{ mr: 1.5, mt: 0.2, color: '#388e3c', fontSize: 18 }} />
                                                                        {uarData.SOD_CHECK === "Yes" ? 
                                                                            "Segregation of Duties controls are included in the access review process to identify conflicting role assignments or permissions." : 
                                                                            "Segregation of Duties is not currently part of the access review process."}
                                                                        {uarData.SOD_MITIGATION && uarData.SOD_MITIGATION !== "N/A" && (
                                                                            <><br /><br /><strong>Mitigation Controls:</strong> {uarData.SOD_MITIGATION}</>
                                                                        )}
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            </mui.Box>
                                                        )}
                                                        
                                                        {uarData.SCOPE_EXEMPTED && uarData.SCOPE_EXEMPTED !== "N/A" && (
                                                            <mui.Box sx={{ mb: 3 }}>
                                                                <SectionSubtitle>Exceptions and Special Considerations</SectionSubtitle>
                                                                
                                                                <mui.Box sx={{ p: 2, backgroundColor: alpha('#fff9c4', 0.5), borderRadius: 2, border: '1px solid #fff59d', mb: 3 }}>
                                                                    <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <ReportProblemIcon sx={{ mr: 1.5, mt: 0.2, color: '#f57f17', fontSize: 18 }} />
                                                                        The following accounts or roles are exempted from the standard review process: {uarData.SCOPE_EXEMPTED}
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            </mui.Box>
                                                        )}
                                                        
                                                        <mui.Box sx={{ mt: 3, p: 2, backgroundColor: alpha('#4caf50', 0.05), borderRadius: 1, border: `1px solid ${alpha('#4caf50', 0.1)}` }}>
                                                            <mui.Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', fontStyle: 'italic' }}>
                                                                <CheckCircleIcon sx={{ mr: 1, mt: 0.2, color: '#4caf50', fontSize: 18 }} />
                                                                The user access review process ensures that access privileges for {apps.APP_NAME} remain appropriate over time, providing an effective detective control to complement preventive controls such as proper access provisioning and termination procedures.
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    </SectionContent>
                                                ) : (
                                                    <SectionContent>
                                                        <mui.Alert severity="info" sx={{ mb: 2 }}>
                                                            <mui.AlertTitle>User Access Review Information Missing</mui.AlertTitle>
                                                            There is no documentation available for how user access reviews are performed for {apps.APP_NAME}. Periodic review of user access is important to ensure that users maintain the appropriate level of access.
                                                        </mui.Alert>
                                                        
                                                        <mui.Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={() => navigate(`/applications/controls/${id}?section=uar`)}
                                                            startIcon={<SettingsIcon />}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Define User Access Review Process
                                                        </mui.Button>
                                                    </SectionContent>
                                                )}
                                            </SectionCard>
                                        </mui.Box>
                                    </MainPaper>
                                )}
                            </mui.Grid>
                        </mui.Grid>
                    </NarrativeContainer>
                    
                    {/* Add dialog components */}
                    <NarrativeCertificationDialog 
                        open={certificationDialogOpen}
                        onClose={() => setCertificationDialogOpen(false)}
                        appId={id}
                        appName={apps.APP_NAME}
                        versionId={currentVersionId}
                        onCertificationComplete={handleCertificationComplete}
                    />
                    
                    <NarrativeVersionCompare 
                        open={compareDialogOpen}
                        onClose={() => setCompareDialogOpen(false)}
                        versionId1={compareVersionIds[0]}
                        versionId2={compareVersionIds[1]}
                    />
                </mui.Box>
            </ResponsiveContainer>
        );

        return (
            <ThemeProvider theme={theme}>
                {loading ? (
                    <mui.Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                        <LogoLoader 
                            size={80} 
                            message="Loading Process Narrative..." 
                            duration={1.5} 
                        />
                    </mui.Box>
                ) : (
                    <SysOwnerSideBar mainContent={mainContent} />
                )}
            </ThemeProvider>
        );
    }

export default ProcessNarrativeDetails;